import WRAP from "WRAP-JS";
import { _orderBy, parseUTCDate, formatUTCDate } from "../../../utils/FPEUtils";
import LegendAmeyuki from "../legend/LegendAmeyuki";
import { WX_ID_AMEYUKI } from "../../../store/WeatherContentsState";
import TimeController from "../helper/TimeController";
import { configAmeyukiDh, configAmeyukiLayer } from "./config/ConfigAmeyuki";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_AMEYUKI, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";

const MISSING_THRESHOLD = {
  [WX_ID_AMEYUKI]: 0
}

class Ameyuki {
  static NAME = WX_ID_AMEYUKI;

  // 雨雪判定
  dhAmeyuki;
  layerAmeyuki;
  //雨雪判定タイムコントローラー
  ameyukiTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;

  dhAutoUpdate = true;

  validtimeInfo = '---';
  visible = false;

  ready = false;
  etd;
  elapsed = 0;
  dispatcher;
  visible;
  errorMessageInfo;
  transmittance = 47;

  constructor() {
    this.layerAmeyuki = new WRAP.Geo.Layer("AMEYUKI");
    this.layerAmeyuki.setVisible(false);
    this.layerAmeyuki.disableInteraction(true);

    this.ameyukiTimeController = new TimeController(WX_ID_AMEYUKI, {
      label: 'AMEYUKI',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dhAmeyuki = new WRAP.Data(configAmeyukiDh);
      this.dhAmeyuki.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
      this.missingDataObserver.monitoring(WX_ID_AMEYUKI, this.dhAmeyuki, this.layerAmeyuki);

      this.dhAmeyuki.updated('validtime', ref => {
        this._selectValidTime(ref);
        resolve(ref);
      });
      this.dhAmeyuki.updated('timelist', () => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_AMEYUKI,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
    });
  }

  initLayer() {
    this.layerAmeyuki.configure(this.dhAmeyuki, configAmeyukiLayer);
  }

  initTimeController() {
    this.ameyukiTimeController.addLayer(this.layerAmeyuki);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(visible);
  }

  getLayer() {
    return this.layerAmeyuki;
  }

  setVisible(visible) {
    if (!this.ready) {
      return;
    }
    if (this.layerAmeyuki.visible() === visible) {
      return;
    }

    if (visible) {
      this.layerAmeyuki.show();
    } else {
      this.layerAmeyuki.hide();
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this.dhAmeyuki.setAutoUpdate('validtime', enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    return {
      id: Ameyuki.NAME,
      label: 'AMEYUKI',
      baseTime: {
        id: Ameyuki.NAME,
        time: this.validtimeInfo
      },
    }
  }

  getLegend() {
    return {
      id: Ameyuki.NAME,
      label: 'AMEYUKI',
      component: LegendAmeyuki,
      contents: this
    }
  }

  getTimeController() {
    return this.ameyukiTimeController;
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectValidTime(this.dhAmeyuki.retained.validtime);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectValidTime(this.dhAmeyuki.retained.validtime);
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;
    this.autoRefresh = autoFlg;
    if (!this.ready) return;
    this.dhAmeyuki.setAutoUpdate('validtime', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  /**
   * 自動更新モード時のvalid選択
   */
  _selectValidTime(ref) {
    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        //タイムコントローラ操作時にAmeyukiを追加した場合、タイムコントローラの選択時制
        targetTime = this.ameyukiTimeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const vt = this._nearValidTime(ref, targetTime);
    if (vt) {
      if (this.validtimeInfo !== vt) {
        this.validtimeInfo = vt;
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      }
      this._updateDataset();
    }
    this._updateTimeList();
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(data, displayTime, content) {
    const vt = this._nearValidTime(data.retained.validtime, displayTime);
    if (vt) {
      this.validtimeInfo = content.validtime = vt;
      return true;
    }
    return false
  }

  _nearValidTime(timeList, targetTime) {
    if (!timeList || timeList.validtime.length === 0) return null;
    timeList = timeList.validtime;

    let _targetTime;
    if (typeof targetTime === 'number') {
      _targetTime = targetTime
    } else {
      _targetTime = parseUTCDate(targetTime).getTime();
    }

    const tmpTimeList = timeList.reduce((accumulator, validtime) => {
      const validSec = parseUTCDate(validtime).getTime();
      accumulator.push(
        {
          "validtime": validtime,
          "validtime_msec": validSec
        }
      );
      return accumulator;
    }, []);

    this.latestValid = tmpTimeList[0].validtime;

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(_targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    return selectedTime.validtime;
  }

  _updateDataset() {
    if (this.validtimeInfo !== '---') {
      /**
       * WRAP, CloudFrontのキャッシュ対策としてクエリストリングに最新のvalidtimeを付与する
       * timelistが更新された場合にvalidtimeを更新する
       */
      let latestValid;
      if (this.timeList && this.timeList[this.timeList.length - 1] !== this.latestValid) {
        latestValid = this.latestValid;
      } else {
        latestValid = this.validtimeInfo;
      }
      this.layerAmeyuki.set({validtime: this.validtimeInfo, latestValid: latestValid});
    }
  }

  _updateTimeList() {
    let validTimes = this.dhAmeyuki.retained?.validtime?.validtime;
    if (!validTimes) {
      return;
    }

    // 最新から直近過去までのvalidにfilter
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const base = formatUTCDate(now, "%Y%m%dT%H%M%S");
    validTimes = Array.from(validTimes).sort();
    this.timeList = validTimes.filter(vt => {
      return base <= vt;
    });

    if (this.autoRefresh) {
      this.ameyukiTimeController.setCurrentTime(this.validtimeInfo);
    }
    this.ameyukiTimeController.adjustCurrentTime();
    this.ameyukiTimeController.invalidate();
  }

  getTransmittance() {
    return this.transmittance;
  }

  setTransmittance(transmittance) {
    if (this.transmittance === transmittance) {
      return;
    }
    this.transmittance = transmittance;
    const opacity = Math.round(255 / 100 * (100 - this.transmittance));
    configAmeyukiLayer.Attributes.style.default.palette.slice(4, 12).forEach(palette => {
      palette[3] = opacity;
    });
    this.layerAmeyuki.invalidate();
  }
}

export default Ameyuki;